import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TabsContent } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { CHART_COLORS } from './colors';

/**
 * Extracted from CategoryConsole.js (component-split refactor, round 4).
 * Trends tab: monthly L1 category spend line chart plus per-category totals.
 */
export function TrendsTab({ categoryTrends, cs }) {
  return (
    <TabsContent value="trends">
      <Card>
        <CardHeader>
          <CardTitle>Category Level 1 Spend Trends</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">Monthly spend trends for each Level 1 category</p>
        </CardHeader>
        <CardContent>
          {categoryTrends?.trends?.length > 0 ? (
            <div className="space-y-4">
              <ResponsiveContainer width="100%" height={450}>
                <LineChart data={categoryTrends.trends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) => {
                      const [year, month] = v.split('-');
                      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                      return `${monthNames[parseInt(month) - 1]} ${year.slice(2)}`;
                    }}
                  />
                  <YAxis tickFormatter={(v) => `${cs}${(v/1000000).toFixed(1)}M`} />
                  <Tooltip
                    formatter={(value, name) => [`${cs}${value?.toLocaleString() || 0}`, name]}
                    labelFormatter={(label) => {
                      const [year, month] = label.split('-');
                      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                      return `${monthNames[parseInt(month) - 1]} ${year}`;
                    }}
                  />
                  <Legend />
                  {categoryTrends.categories?.map((category, index) => (
                    <Line
                      key={category}
                      type="monotone"
                      dataKey={category}
                      stroke={CHART_COLORS[index % CHART_COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                      name={category}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>

              {/* Category Legend with Totals */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                {categoryTrends.categories?.map((category, index) => {
                  const totalSpend = categoryTrends.trends.reduce((sum, month) => sum + (month[category] || 0), 0);
                  return (
                    <div key={category} className="flex items-center gap-2" data-testid={`trends-legend-${index}`}>
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{category}</p>
                        <p className="text-xs text-muted-foreground font-mono">{cs}{totalSpend.toLocaleString()}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No trend data available</p>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
}
